import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom'
import { convex } from '../lib/convex'
import { api } from '../convex_generated/api'
import { useAuth } from '../hooks/useAuth'
import { AddCommentModal, ViewCommentsModal, type ParagraphComment } from '../components/comments/CommentModals'
import { useToast } from '../contexts/ToastContext'
import { useUI } from '../contexts/UIContext'
import { useMutation, useQuery } from 'convex/react'
import SlateRenderer, { type OriginStoryInfo, type BranchMarker } from '../components/reader/SlateRenderer'
import {
  ArrowLeftIcon,
  ChevronDownIcon,
  ExclamationCircleIcon,
  FlagIcon,
  LockClosedIcon,
  MinusIcon,
  PlusIcon,
  ScissorsIcon,
  PencilIcon,
  BookOpenIcon,
  CameraIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline'
import { StarIcon as StarSolid } from '@heroicons/react/24/solid'
import { BOOK_GENRES, GENRE_BADGE_COLORS } from '../constants/genres'
import { AI_TIER_OPTIONS, type AiTier } from '../lib/aiTiers'
import { useGenerationProgress } from '../lib/useGenerationProgress'
import FollowContentButton from '../components/social/FollowContentButton'
import ImmersiveReader from '../components/reader/ImmersiveReader'
import { asId } from '../lib/ids'
import type { Doc, Id } from '../convex_generated/dataModel'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Branch {
  _id: string
  title: string
  parentBranchId?: string
  category: string
  visibility: string
  price?: number
  rating: number
  ratingCount: number
  wordCount: number
  authorId: string
  forkPosition?: number   // character offset in root text
  forkContext?: string
  publishedAt?: number
  isImported?: boolean
  // linked-branch placement in the target book
  targetBookId?: string
  targetForkPosition?: number
  targetForkContext?: string
}

type BranchCategory = 'backstory' | 'parallel-worlds' | 'expanded-reality' | 'general' | 'origin-story' | 'linked-branch'
type Genre = Doc<'books'>['genre'][number]

interface Book {
  _id: string
  title: string
  originalAuthor: string
  description: string
  coverImage: string
  genre: string[]
  rootBranchId: string
  importedBy?: string
  sourceUrl?: string
  gutenbergId?: string
  inProgress?: boolean
}

interface Chapter {
  title: string
  charOffset: number
  domIndex: number  // index among h1/h2/h3 in rendered DOM
  level: 1 | 2 | 3
}

// ---------------------------------------------------------------------------
// Category colours
// ---------------------------------------------------------------------------
const CATEGORY_COLOR: Record<string, { bg: string; text: string; dot: string }> = {
  general:            { bg: 'bg-blue-100 dark:bg-blue-900/40',      text: 'text-blue-700 dark:text-blue-300',      dot: '#3b82f6' },
  backstory:          { bg: 'bg-purple-100 dark:bg-purple-900/40',  text: 'text-purple-700 dark:text-purple-300',  dot: '#a855f7' },
  'parallel-worlds':  { bg: 'bg-green-100 dark:bg-green-900/40',    text: 'text-green-700 dark:text-green-300',    dot: '#22c55e' },
  'expanded-reality': { bg: 'bg-orange-100 dark:bg-orange-900/40',  text: 'text-orange-700 dark:text-orange-300',  dot: '#f97316' },
  'origin-story':     { bg: 'bg-rose-100 dark:bg-rose-900/40',      text: 'text-rose-700 dark:text-rose-300',      dot: '#f43f5e' },
  'linked-branch':    { bg: 'bg-cyan-100 dark:bg-cyan-900/40',      text: 'text-cyan-700 dark:text-cyan-300',      dot: '#06b6d4' },
}
const CATEGORY_DESCRIPTIONS: Record<string, string> = {
  general:            'Continue or reimagine the story freely',
  backstory:          'Explore what happened before this moment',
  'parallel-worlds':  'Take the narrative into a different reality',
  'expanded-reality': 'Zoom into a side character, place, or event',
  'origin-story':     'Tell the backstory of a specific character',
  'linked-branch':    'A bridge connecting this story to another book',
}
const DEFAULT_COLOR = { bg: 'bg-gray-100 dark:bg-gray-700', text: 'text-gray-700 dark:text-gray-300', dot: '#6b7280' }
const cat = (c: string) => CATEGORY_COLOR[c] ?? DEFAULT_COLOR

// Detect touch/mobile devices (evaluated once at module load)
const isTouchDevice = typeof window !== 'undefined' &&
  ('ontouchstart' in window || navigator.maxTouchPoints > 0)

// ---------------------------------------------------------------------------
// Git-lane graph
// ---------------------------------------------------------------------------

const TRUNK_Y = 82        // y-centre of the main trunk line
const GENRE_COLORS: Record<string, string> = GENRE_BADGE_COLORS

const GRAPH_H = 140       // total SVG height
const ABOVE_Y = 10        // top of above-trunk branch label foreignObjects
const BELOW_Y = 112       // top of below-trunk branch label foreignObjects
const LABEL_H = 44        // height of branch label foreignObjects
const CHAPTER_R = 4       // chapter dot radius

interface GraphProps {
  rootBranch: Branch
  branches: Branch[]        // non-root user branches
  chapters: Chapter[]       // heading positions in root content
  totalChars: number
  containerWidth: number    // measured container px width
  onBranchClick: (branch: Branch) => void
  onChapterClick: (chapter: Chapter) => void
  activeBranchId?: string
  userId: string
  readingProgress?: number  // 0-1 fraction of scroll through main text
}

const GitLaneGraph: React.FC<GraphProps> = ({
  rootBranch, branches, chapters, totalChars, containerWidth,
  onBranchClick, onChapterClick, activeBranchId, userId, readingProgress,
}) => {
  const [hoveredChapter, setHoveredChapter] = useState<number | null>(null)

  // Visible user branches (public/paid + own drafts)
  const visible = branches.filter(b => b.visibility !== 'private' || b.authorId === userId)
  const sorted  = [...visible].sort((a, b) => (a.forkPosition ?? 0) - (b.forkPosition ?? 0))
  const laned   = sorted.map((b, i) => ({ branch: b, lane: i % 2 === 0 ? -1 : 1 }))

  // Width: fill container, expanding right only if needed for label overflow
  const minFromBranches = laned.reduce((acc, { branch }) => {
    const x = 40 + (branch.forkPosition ?? 0) * ((containerWidth - 80) / Math.max(1, totalChars))
    return Math.max(acc, x + 80)
  }, containerWidth)
  const svgWidth = Math.max(containerWidth, minFromBranches)

  // Scale: map char offset → x pixel
  const scale    = (svgWidth - 80) / Math.max(1, totalChars)
  const charToX  = (pos: number) => 40 + pos * scale

  const trunkEnd = svgWidth - 20

  return (
    <div className="overflow-x-auto select-none w-full" style={{ height: GRAPH_H }}>
      <svg width={svgWidth} height={GRAPH_H} style={{ display: 'block', minWidth: svgWidth }}>

        {/* ── Trunk line ── */}
        <line x1={40} y1={TRUNK_Y} x2={trunkEnd} y2={TRUNK_Y}
          stroke="#d97706" strokeWidth={3} strokeLinecap="round" />

        {/* ── Root node ── */}
        <g className="cursor-pointer" onClick={() => onBranchClick(rootBranch)}>
          <circle cx={48} cy={TRUNK_Y} r={10}
            fill={activeBranchId === rootBranch._id ? '#d97706' : '#fff'}
            stroke="#d97706" strokeWidth={2.5}
          />
          <text x={48} y={TRUNK_Y + 20} textAnchor="middle"
            style={{ fontSize: 10, fontWeight: 600, fill: '#b45309' }}>
            Main
          </text>
        </g>

        {/* ── Terminal dot ── */}
        <circle cx={trunkEnd} cy={TRUNK_Y} r={5} fill="#d97706" opacity={0.35} />

        {/* ── Chapter ticks + dots ── */}
        {chapters.map((ch, i) => {
          const x    = charToX(ch.charOffset)
          const size = ch.level === 1 ? CHAPTER_R + 1 : ch.level === 2 ? CHAPTER_R : CHAPTER_R - 1
          const isHovered = hoveredChapter === i
          return (
            <g key={`ch-${i}`}
              className="cursor-pointer"
              onClick={() => onChapterClick(ch)}
              onMouseEnter={() => setHoveredChapter(i)}
              onMouseLeave={() => setHoveredChapter(null)}
            >
              {/* Tick line through trunk */}
              <line x1={x} y1={TRUNK_Y - size - 4} x2={x} y2={TRUNK_Y + size + 4}
                stroke="#1e40af" strokeWidth={ch.level === 1 ? 1.5 : 1} opacity={0.5} />
              {/* Dot */}
              <circle cx={x} cy={TRUNK_Y} r={size}
                fill={isHovered ? '#1e40af' : '#fff'}
                stroke="#1e40af" strokeWidth={1.5} opacity={isHovered ? 1 : 0.7}
              />
              {/* Chapter number below */}
              {ch.level === 1 && (
                <text x={x} y={TRUNK_Y + size + 14} textAnchor="middle"
                  style={{ fontSize: 9, fill: '#3b82f6', fontWeight: 500, opacity: 0.75 }}>
                  {i + 1}
                </text>
              )}
              {/* Hover label */}
              {isHovered && (
                <g>
                  <rect x={Math.min(x - 4, svgWidth - 160)} y={TRUNK_Y + CHAPTER_R + 18}
                    width={160} height={20} rx={4}
                    fill="#1e3a8a" opacity={0.92}
                  />
                  <text x={Math.min(x + 76, svgWidth - 4)} y={TRUNK_Y + CHAPTER_R + 32}
                    textAnchor="end"
                    style={{ fontSize: 10, fill: '#fff', fontWeight: 500 }}>
                    {ch.title.length > 24 ? ch.title.slice(0, 24) + '…' : ch.title}
                  </text>
                </g>
              )}
            </g>
          )
        })}

        {/* ── Branch connectors + labels ── */}
        {laned.map(({ branch, lane }) => {
          const x        = charToX(branch.forkPosition ?? 0)
          const color    = cat(branch.category).dot
          const isActive = branch._id === activeBranchId
          const labelY   = lane === -1 ? ABOVE_Y : BELOW_Y
          const midY     = lane === -1 ? TRUNK_Y - 30 : TRUNK_Y + 30
          const connEnd  = lane === -1 ? labelY + LABEL_H : labelY
          // Clamp label x so it doesn't clip out of SVG
          const labelX   = Math.max(4, Math.min(svgWidth - 148, x - 70))

          return (
            <g key={branch._id} className="cursor-pointer" onClick={() => onBranchClick(branch)}>
              {/* Curved connector */}
              <path
                d={`M ${x} ${TRUNK_Y} C ${x} ${midY}, ${x} ${midY}, ${x} ${connEnd}`}
                fill="none" stroke={color}
                strokeWidth={isActive ? 2.5 : 1.5}
                strokeDasharray={branch.visibility === 'private' ? '4 3' : undefined}
                opacity={0.75}
              />
              {/* Dot on trunk (portal icon for linked branches) */}
              {branch.category === 'linked-branch' ? (
                <g>
                  <circle cx={x} cy={TRUNK_Y} r={8}
                    fill={isActive ? color : '#fff'}
                    stroke={color} strokeWidth={2}
                  />
                  {/* Small link/portal arrows icon */}
                  <path d={`M ${x-3.5} ${TRUNK_Y-2} l 3 -3 3 3 M ${x+3.5} ${TRUNK_Y+2} l -3 3 -3 -3`}
                    fill="none" stroke={isActive ? '#fff' : color} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"
                  />
                </g>
              ) : (
                <circle cx={x} cy={TRUNK_Y} r={7}
                  fill={isActive ? color : '#fff'}
                  stroke={color} strokeWidth={2}
                />
              )}
              {/* Label pill via foreignObject */}
              <foreignObject x={labelX} y={labelY} width={144} height={LABEL_H}>
                <div
                  // @ts-expect-error foreignObject needs xmlns but React typings don't include it
                  xmlns="http://www.w3.org/1999/xhtml"
                  className={`rounded-lg px-2 py-1 text-[11px] leading-tight border transition-all ${
                    isActive
                      ? 'border-current shadow-md ring-1 ring-current'
                      : 'border-gray-200 dark:border-gray-600 hover:border-current'
                  } ${cat(branch.category).bg} ${cat(branch.category).text}`}
                  style={{ cursor: 'pointer', height: LABEL_H - 4 }}
                >
                  <div className="font-semibold truncate">{branch.title}</div>
                  <div className="opacity-70 flex items-center gap-1 mt-0.5 text-[10px]">
                    {branch.visibility === 'paid' && <LockClosedIcon style={{ width: 9, height: 9, display: 'inline' }} />}
                    {branch.visibility === 'private' && <span className="italic">draft</span>}
                    <span>{branch.category.replace('-', ' ')}</span>
                  </div>
                </div>
              </foreignObject>
            </g>
          )
        })}

        {/* ── Reading position playhead ── */}
        {readingProgress !== undefined && (() => {
          const px = Math.round(charToX(readingProgress * Math.max(1, totalChars)))
          return (
            <g className="pointer-events-none" key="playhead">
              <line x1={px} y1={TRUNK_Y - 18} x2={px} y2={TRUNK_Y + 18}
                stroke="#fbbf24" strokeWidth={2.5} strokeLinecap="round" opacity={0.9} />
              <polygon
                points={`${px - 5},${TRUNK_Y - 18} ${px + 5},${TRUNK_Y - 18} ${px},${TRUNK_Y - 10}`}
                fill="#fbbf24" opacity={0.9}
              />
            </g>
          )
        })()}
      </svg>
    </div>
  )
}

// ---------------------------------------------------------------------------
// AI cover generation modal
// ---------------------------------------------------------------------------

// Keep prices in sync with convex/aiCover.ts COVER_TIERS.
type CoverTier = 'flash' | 'pro'
const COVER_TIERS: { id: CoverTier; label: string; price: number; desc: string }[] = [
  { id: 'flash', label: 'Flash', price: 40, desc: 'Fast, lower cost' },
  { id: 'pro', label: 'Pro', price: 90, desc: 'Highest quality' },
]

const CoverModal: React.FC<{
  bookId: string
  balance: number
  onClose: () => void
  onDone: () => void
}> = ({ bookId, balance, onClose, onDone }) => {
  const [prompt, setPrompt] = useState('')
  const [loadingPrompt, setLoadingPrompt] = useState(true)
  const [tier, setTier] = useState<CoverTier>('flash')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { label: progressLabel, elapsed } = useGenerationProgress(busy)
  const price = COVER_TIERS.find((t) => t.id === tier)!.price
  const insufficient = balance < price

  useEffect(() => {
    let cancelled = false
    setLoadingPrompt(true)
    convex
      .action(api.aiCover.suggestCoverPrompt, { bookId: asId<"books">(bookId) })
      .then((r) => { if (!cancelled) setPrompt(r?.prompt ?? '') })
      .catch(() => { /* user can write their own */ })
      .finally(() => { if (!cancelled) setLoadingPrompt(false) })
    return () => { cancelled = true }
  }, [bookId])

  const handleGenerate = async () => {
    if (!prompt.trim()) { setError('Describe the cover art'); return }
    if (insufficient) { setError(`You need ${price} StoryCoins (you have ${balance})`); return }
    setBusy(true)
    setError(null)
    try {
      await convex.action(api.aiCover.generateBookCover, { bookId: asId<"books">(bookId), prompt: prompt.trim(), tier })
      onDone()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Generation failed')
      setBusy(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={(e) => e.target === e.currentTarget && !busy && onClose()}>
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-5 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <SparklesIcon className="w-5 h-5 text-violet-600" />
            <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">Generate a cover</h2>
          </div>
          <button onClick={onClose} disabled={busy} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 disabled:opacity-40">✕</button>
        </div>

        {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

        <div>
          <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">Cover art prompt</label>
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            rows={5}
            maxLength={2000}
            disabled={loadingPrompt}
            placeholder={loadingPrompt ? 'Suggesting a prompt from the book…' : 'Describe the cover art…'}
            className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400 resize-none disabled:opacity-60"
          />
          <p className="mt-1 text-[11px] text-gray-400">Art only — the title and author are shown over the cover, not drawn into it.</p>
        </div>

        <div>
          <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Quality</label>
          <div className="grid grid-cols-2 gap-2">
            {COVER_TIERS.map((t) => (
              <button
                key={t.id}
                onClick={() => setTier(t.id)}
                className={`p-2.5 rounded-xl border-2 text-center transition-all ${tier === t.id ? 'border-violet-500 bg-violet-50 dark:bg-violet-900/20' : 'border-gray-200 dark:border-gray-700 hover:border-violet-300'}`}
              >
                <p className={`text-xs font-semibold ${tier === t.id ? 'text-violet-700 dark:text-violet-300' : 'text-gray-700 dark:text-gray-300'}`}>{t.label}</p>
                <p className="text-[10px] text-gray-500 dark:text-gray-400">{t.desc}</p>
                <p className="text-[11px] font-semibold text-gray-700 dark:text-gray-300 mt-1">{t.price} SC</p>
              </button>
            ))}
          </div>
        </div>

        <p className={`text-xs ${insufficient ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'}`}>
          Balance: {balance} StoryCoins{insufficient ? ' — not enough' : ''}. You won't be charged if generation fails. This replaces the current cover.
        </p>

        {busy && (
          <div className="flex items-center gap-2.5 rounded-xl bg-violet-50 dark:bg-violet-900/20 px-3 py-2.5">
            <span className="w-4 h-4 rounded-full border-2 border-violet-300 border-t-violet-600 animate-spin" />
            <span className="text-sm font-medium text-violet-700 dark:text-violet-300">{progressLabel}</span>
            <span className="ml-auto text-xs tabular-nums text-violet-400">{elapsed}s</span>
          </div>
        )}

        <div className="flex gap-3 pt-1">
          <button onClick={onClose} disabled={busy} className="flex-1 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50">Cancel</button>
          <button
            onClick={handleGenerate}
            disabled={busy || loadingPrompt || insufficient}
            className="flex-1 py-2.5 bg-violet-600 hover:bg-violet-700 disabled:bg-violet-400 text-white text-sm font-semibold rounded-xl transition-colors flex items-center justify-center gap-1.5"
          >
            <SparklesIcon className="w-4 h-4" />
            {busy ? 'Generating…' : `Generate · ${price} SC`}
          </button>
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main BookPage
// ---------------------------------------------------------------------------

const BookPage: React.FC = () => {
  const { bookId } = useParams<{ bookId: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate   = useNavigate()
  const { user, canModerate }   = useAuth()
  const { showToast }    = useToast()
  const { openAuthModal, readingFontSize: fontSize, setReadingFontSize } = useUI()
  const isSignedIn        = !!user

  // ── Reactive data (auto-updates when branches are added/published) ──
  const bookData     = useQuery(api.books.getBook,            bookId ? { bookId: asId<"books">(bookId) } : 'skip') as Book | null | undefined
  const branchesData = useQuery(api.branches.getBranchesForBook, bookId ? { bookId: asId<"books">(bookId) } : 'skip') as Branch[] | undefined
  const originStoriesData = useQuery(api.branches.getOriginStoriesForBook, bookId ? { bookId: asId<"books">(bookId) } : 'skip') as Array<{ characterName: string; stories: OriginStoryInfo[] }> | undefined
  const incomingLinksData = useQuery(api.branches.getLinkedBranchesForBook, bookId ? { bookId: asId<"books">(bookId) } : 'skip') as Array<{
    _id: string; title: string; bookId: string; sourceBookTitle: string; sourceBookCover: string | null;
    authorName: string; wordCount: number; rating: number; ratingCount: number;
    forkPosition?: number; forkContext?: string; targetForkPosition?: number; targetForkContext?: string;
  }> | undefined

  const book: Book | null         = bookData ?? null
  const allBranches: Branch[]     = branchesData ?? []
  const rootBranch                = allBranches.find(b => !b.parentBranchId)
  const userBranches              = allBranches.filter(b => !!b.parentBranchId)
  const isLoading                 = bookData === undefined || branchesData === undefined

  // ── Selected branch (defaults to root once loaded) ──
  const [activeBranchId, setActiveBranchId] = useState<string | null>(null)
  useEffect(() => {
    if (!activeBranchId && rootBranch) setActiveBranchId(rootBranch._id)
  }, [rootBranch, activeBranchId])
  const activeBranch = allBranches.find(b => b._id === activeBranchId) ?? rootBranch ?? null

  // ── Root branch content (fetched once; panel always shows main text) ──
  const rootBranchData = useQuery(
    api.branches.getBranch,
    rootBranch ? { branchId: asId<"branches">(rootBranch._id) } : 'skip'
  )

  const [contentNodes, setContentNodes]       = useState<unknown[]>([])
  const [isLoadingContent, setIsLoadingContent] = useState(false)

  // Font size controls
  type FontSize = 'sm' | 'base' | 'lg' | 'xl'
  const FONT_SIZES: FontSize[] = ['sm', 'base', 'lg', 'xl']
  const FONT_SIZE_LABELS: Record<FontSize, string> = { sm: 'Small', base: 'Medium', lg: 'Large', xl: 'Extra Large' }
  const setFontSize = (s: FontSize) => setReadingFontSize(s)
  const fontSizeIdx = FONT_SIZES.indexOf(fontSize)
  const [rootCharCount, setRootCharCount]     = useState(0)
  const [chapters, setChapters]               = useState<Chapter[]>([])

  // ── Immersive reading mode ──
  const [readingMode, setReadingMode] = useState(false)

  // Fetch root content – either from storage URL or inline content field
  const contentUrl = rootBranchData?.contentUrl as string | undefined
  const inlineContent = rootBranchData?.content as unknown[] | undefined
  useEffect(() => {
    if (contentUrl) {
      setIsLoadingContent(true)
      fetch(contentUrl)
        .then(r => r.json())
        .then((nodes: unknown[]) => {
          setContentNodes(nodes)
          setRootCharCount(extractText(nodes).length)
          setChapters(extractChapters(nodes))
        })
        .catch(() => {})
        .finally(() => setIsLoadingContent(false))
    } else if (Array.isArray(inlineContent) && inlineContent.length > 0) {
      setContentNodes(inlineContent)
      setRootCharCount(extractText(inlineContent).length)
      setChapters(extractChapters(inlineContent))
    }
  }, [contentUrl, inlineContent])

  // ── Container width measurement → trunk fills screen ──
  const graphContainerRef = useRef<HTMLDivElement>(null)
  const [containerWidth, setContainerWidth] = useState(800)
  useLayoutEffect(() => {
    if (!graphContainerRef.current) return
    const ro = new ResizeObserver(entries => {
      const w = entries[0]?.contentRect.width
      if (w) setContainerWidth(w)
    })
    ro.observe(graphContainerRef.current)
    return () => ro.disconnect()
  }, [])

  // ── Scroll refs ──
  const contentRef      = useRef<HTMLDivElement>(null)
  const contentPanelRef = useRef<HTMLDivElement>(null)
  const graphSectionRef = useRef<HTMLDivElement>(null)

  // ── "Branch here" floating button (appears after 2s scroll pause) ──
  const [branchHereVisible, setBranchHereVisible] = useState(false)
  const [branchHereAnchor, setBranchHereAnchor] = useState<{ text: string; charOffset: number } | null>(null)
  const scrollPauseTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [readingProgress, setReadingProgress] = useState(0)
  const [mobileSelectionBar, setMobileSelectionBar] = useState<{ text: string; charOffset: number } | null>(null)
  const [graphCollapsed, setGraphCollapsed] = useState(false)

  // ── Comment support for root branch reader ──
  const [addCommentState, setAddCommentState] = useState<{ paragraphIndex: number; replyTo?: { id: string; authorName: string } } | null>(null)
  const [viewCommentsParagraph, setViewCommentsParagraph] = useState<number | null>(null)

  const isInProgress = rootBranch?.visibility === 'in-progress'
  const isPublishedBranch = rootBranch?.visibility === 'public'
  const isPublicDomainBranch = isPublishedBranch && !!rootBranch?.isImported
  const commentsEnabled = isInProgress || isPublishedBranch
  const canBranchPerParagraph = isPublishedBranch

  const rawComments = useQuery(
    api.comments.getCommentsForBranch,
    rootBranch && commentsEnabled ? { branchId: asId<"branches">(rootBranch._id) } : 'skip',
  )
  const comments = useMemo(() => (rawComments ?? []) as ParagraphComment[], [rawComments])

  const commentCounts = useMemo(() => {
    if (!comments.length) return undefined
    const map = new Map<number, number>()
    for (const c of comments) {
      map.set(c.paragraphIndex, (map.get(c.paragraphIndex) ?? 0) + 1 + c.replies.length)
    }
    return map
  }, [comments])

  const addCommentMutation = useMutation(api.comments.addComment)
  const deleteCommentMutation = useMutation(api.comments.deleteComment)
  const flagCommentMutation = useMutation(api.comments.flagComment)
  const likeCommentMutation = useMutation(api.comments.likeComment)
  const dislikeCommentMutation = useMutation(api.comments.dislikeComment)

  useEffect(() => {
    const panel = contentPanelRef.current
    if (!panel) return
    const onScroll = () => {
      // Update reading position playhead in git graph
      const progress = panel.scrollTop / Math.max(1, panel.scrollHeight - panel.clientHeight)
      setReadingProgress(Math.min(1, Math.max(0, progress)))
      setBranchHereVisible(false)
      if (scrollPauseTimer.current) clearTimeout(scrollPauseTimer.current)
      scrollPauseTimer.current = setTimeout(() => {
        if (!contentRef.current) return
        const panelRect = panel.getBoundingClientRect()
        const centerY = panelRect.top + panelRect.height / 2
        const paras = Array.from(contentRef.current.querySelectorAll('p'))
        let best: Element | null = null
        let bestDist = Infinity
        for (const p of paras) {
          const r = p.getBoundingClientRect()
          if (r.height === 0 || !p.textContent?.trim()) continue
          const dist = Math.abs((r.top + r.bottom) / 2 - centerY)
          if (dist < bestDist) { bestDist = dist; best = p }
        }
        if (!best || !contentRef.current) return
        const text = best.textContent?.trim().slice(0, 200) ?? ''
        if (text.length < 15) return
        const anchorRange = document.createRange()
        anchorRange.selectNodeContents(best)
        anchorRange.collapse(true)
        const charOffset = estimateCharOffset(contentRef.current, anchorRange)
        setBranchHereAnchor({ text, charOffset })
        setBranchHereVisible(true)
      }, 2000)
    }
    panel.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      panel.removeEventListener('scroll', onScroll)
      if (scrollPauseTimer.current) clearTimeout(scrollPauseTimer.current)
    }
  // Re-run when the book loads: on first render the panel isn't in the DOM yet
  // (component shows loading spinner), so the ref is null and listener never attaches.
  }, [book?._id])

  const handleBranchHereClick = useCallback(() => {
    if (!isSignedIn) { openAuthModal(); return }
    if (!branchHereAnchor) return
    setForkPopover({ x: 0, y: 0, text: branchHereAnchor.text, charOffset: branchHereAnchor.charOffset })
    setBranchHereVisible(false)
    setForkTitle('')
    setForkModalOpen(true)
  }, [isSignedIn, openAuthModal, branchHereAnchor])

  // ── mutations + queries ──
  const purchaseBranch      = useMutation(api.payments.purchaseBranch)
  const fileReport          = useMutation(api.reports.fileReport)
  const generateUploadUrl   = useMutation(api.books.generateCoverUploadUrl)
  const updateBookMetadata  = useMutation(api.books.updateBookMetadata)

  // ── Edit-book modal ──
  const coverFileRef = useRef<HTMLInputElement>(null)
  const [editModal, setEditModal] = useState<{
    title: string; author: string; description: string; sourceUrl: string
    genres: string[]
    inProgress: boolean
    coverPreview: string | null   // local object URL for preview
    coverFile: File | null
    saving: boolean
  } | null>(null)

  // Only admins can edit metadata of imported books
  const isImportedBook = !!book?.sourceUrl || !!book?.gutenbergId || !!book?.importedBy
  const isBookEditor = isSignedIn && isImportedBook && canModerate()

  const openEditModal = useCallback(() => {
    if (!book) return
    setEditModal({
      title: book.title,
      author: book.originalAuthor,
      description: book.description ?? '',
      sourceUrl: book.sourceUrl ?? '',
      genres: book.genre?.length ? book.genre : ['fiction'],
      inProgress: !!book.inProgress,
      coverPreview: null,
      coverFile: null,
      saving: false,
    })
  }, [book])

  const handleCoverFilePick = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const previewUrl = URL.createObjectURL(file)
    setEditModal(m => m ? { ...m, coverFile: file, coverPreview: previewUrl } : null)
  }, [])

  const handleEditSave = useCallback(async () => {
    if (!editModal || !book) return
    setEditModal(m => m ? { ...m, saving: true } : null)
    try {
      let coverStorageId: Id<'_storage'> | undefined
      if (editModal.coverFile) {
        const uploadUrl = await generateUploadUrl({})
        const res = await fetch(uploadUrl, {
          method: 'POST',
          headers: { 'Content-Type': editModal.coverFile.type },
          body: editModal.coverFile,
        })
        const { storageId } = await res.json()
        coverStorageId = storageId
      }
      await updateBookMetadata({
        bookId: asId<"books">(book._id),
        title: editModal.title || undefined,
        originalAuthor: editModal.author || undefined,
        description: editModal.description || undefined,
        sourceUrl: editModal.sourceUrl || undefined,
        genre: editModal.genres.length > 0 ? (editModal.genres as Genre[]) : undefined,
        inProgress: editModal.inProgress,
        ...(coverStorageId ? { coverStorageId } : {}),
      })
      if (editModal.coverPreview) URL.revokeObjectURL(editModal.coverPreview)
      setEditModal(null)
      showToast({ message: 'Book details updated.', type: 'success' })
    } catch (e) {
      setEditModal(m => m ? { ...m, saving: false } : null)
      showToast({ message: e instanceof Error ? e.message : 'Failed to save', type: 'error' })
    }
  }, [editModal, book, generateUploadUrl, updateBookMetadata, showToast])

  // ── Flag / report modal ──
  const [flagModal, setFlagModal] = useState<{
    type: 'book' | 'branch'
    targetId: string
    reason: 'copyright' | 'plagiarism' | 'other'
    details: string
    submitting: boolean
  } | null>(null)

  const bookReport   = useQuery(
    api.reports.getMyReport,
    book && isSignedIn ? { bookId: asId<"books">(book._id) } : 'skip',
  ) as { _id: string } | null | undefined

  const branchReport = useQuery(
    api.reports.getMyReport,
    activeBranch && isSignedIn && activeBranch.parentBranchId
      ? { branchId: asId<"branches">(activeBranch._id) }
      : 'skip',
  ) as { _id: string } | null | undefined

  // ── Branch rating ──
  const rateBranch      = useMutation(api.branches.rateBranch)
  const myBranchRating  = useQuery(
    api.branches.getMyRatingForBranch,
    activeBranch && isSignedIn ? { branchId: asId<"branches">(activeBranch._id) } : 'skip',
  ) as { rating: number } | null | undefined
  const [ratingHover, setRatingHover] = useState<number>(0)

  const handleFlagSubmit = useCallback(async () => {
    if (!flagModal) return
    setFlagModal(m => m ? { ...m, submitting: true } : null)
    try {
      await fileReport({
        ...(flagModal.type === 'book'
          ? { bookId: asId<"books">(flagModal.targetId) }
          : { branchId: asId<"branches">(flagModal.targetId) }),
        reason: flagModal.reason,
        details: flagModal.details.trim() || undefined,
      })
      setFlagModal(null)
      showToast({ message: 'Report submitted — our team will review it shortly.', type: 'success' })
    } catch (e) {
      setFlagModal(m => m ? { ...m, submitting: false } : null)
      showToast({ message: e instanceof Error ? e.message : 'Failed to submit report', type: 'error' })
    }
  }, [flagModal, fileReport, showToast])

  // ── Branch click: scroll the main text to a fork position ──
  // Tries a whitespace-tolerant text match on the fork excerpt; falls back to the
  // stored character offset (forkPosition) when the excerpt can't be matched —
  // e.g. spacing differs, highlighting split the paragraph into multiple text
  // nodes, or the branch has no excerpt.
  const scrollToText = useCallback((needle: string, forkPosition?: number) => {
    const panel = contentPanelRef.current
    const root = contentRef.current
    if (!panel || !root) return

    const graphOffset = graphSectionRef.current?.offsetHeight ?? 0
    const PADDING = 16
    const scrollToNode = (node: Node) => {
      const range = document.createRange()
      range.selectNode(node)
      const panelRect = panel.getBoundingClientRect()
      const elRect = range.getBoundingClientRect()
      panel.scrollTo({
        top: panel.scrollTop + elRect.top - panelRect.top - graphOffset - PADDING,
        behavior: 'smooth',
      })
    }

    const norm = (s: string) => s.replace(/\s+/g, ' ').trim()
    const target = norm(needle).slice(0, 40)

    // Pass 1: whitespace-tolerant substring match within a single text node.
    if (target.length >= 4) {
      const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT)
      let node: Text | null
      while ((node = walker.nextNode() as Text | null)) {
        if (node.textContent && norm(node.textContent).includes(target)) {
          scrollToNode(node)
          return
        }
      }
    }

    // Pass 2: fall back to the character offset into the rendered text.
    if (typeof forkPosition === 'number' && forkPosition >= 0) {
      const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT)
      let acc = 0
      let node: Text | null
      while ((node = walker.nextNode() as Text | null)) {
        const len = node.textContent?.length ?? 0
        if (acc + len >= forkPosition) {
          scrollToNode(node)
          return
        }
        acc += len
      }
    }
  }, [])

  // ── Auto-scroll to ?scrollTo= param (used by linked branch bridges) ──
  const scrollToParam = searchParams.get('scrollTo')
  const [didAutoScroll, setDidAutoScroll] = useState(false)
  useEffect(() => {
    if (!scrollToParam || didAutoScroll || !contentRef.current) return
    // Wait a tick for DOM to render
    const timer = setTimeout(() => {
      scrollToText(scrollToParam)
      setDidAutoScroll(true)
      // Clean URL
      searchParams.delete('scrollTo')
      setSearchParams(searchParams, { replace: true })
    }, 500)
    return () => clearTimeout(timer)
  }, [scrollToParam, didAutoScroll, scrollToText, searchParams, setSearchParams])

  const handleBranchClick = useCallback((branch: Branch) => {
    setActiveBranchId(branch._id)
    if (!branch.parentBranchId) {
      // Root branch — scroll to top
      contentPanelRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
    } else if (branch.forkContext || typeof branch.forkPosition === 'number') {
      // User branch — flow the main text to its fork point (excerpt match, else offset)
      scrollToText(branch.forkContext ?? '', branch.forkPosition)
    }
  }, [scrollToText])

  // ── Chapter click: scroll to heading element in DOM ──
  const handleChapterClick = useCallback((chapter: Chapter) => {
    if (!contentRef.current || !contentPanelRef.current) return
    const headings = contentRef.current.querySelectorAll('h1, h2, h3')
    const el = headings[chapter.domIndex] as HTMLElement | undefined
    if (!el) return
    const panelRect = contentPanelRef.current.getBoundingClientRect()
    const elRect    = el.getBoundingClientRect()
    const graphOffset = graphSectionRef.current?.offsetHeight ?? 0
    const PADDING = 8
    contentPanelRef.current.scrollTo({
      top: contentPanelRef.current.scrollTop + elRect.top - panelRect.top - graphOffset - PADDING,
      behavior: 'smooth',
    })
  }, [])

  // ── Fork popover on text selection ──
  const [forkPopover, setForkPopover] = useState<{ x: number; y: number; text: string; charOffset: number } | null>(null)
  const [forkModalOpen, setForkModalOpen] = useState(false)
  const [forkTitle,    setForkTitle]    = useState('')
  const [forkCategory, setForkCategory] = useState<BranchCategory>('general')
  const [manualExcerpt, setManualExcerpt] = useState('')  // used when branching without text selection
  const [forkCharacterName, setForkCharacterName] = useState('')  // used for origin-story category
  // AI writing mode (not available for linked-branch, which the generator doesn't support)
  const [forkMode, setForkMode] = useState<'manual' | 'ai'>('manual')
  const [forkBrief, setForkBrief] = useState('')
  const [forkTier, setForkTier] = useState<AiTier>('standard')
  const [aiBusy, setAiBusy] = useState(false)
  const { label: aiProgressLabel, elapsed: aiElapsed } = useGenerationProgress(aiBusy && forkMode === 'ai')
  const me = useQuery(api.users.getMe)
  const storyCoinBalance = me?.storyCoins ?? 0
  const [coverModalOpen, setCoverModalOpen] = useState(false)
  // Linked-branch state
  const [forkTargetBookId, setForkTargetBookId] = useState<string | null>(null)
  const [forkTargetBookTitle, setForkTargetBookTitle] = useState('')
  const [forkTargetContext, setForkTargetContext] = useState('')  // text excerpt on target book where bridge lands
  const [bookSearchQuery, setBookSearchQuery] = useState('')
  const [bookSearchOpen, setBookSearchOpen] = useState(false)

  // Book search query for linked-branch picker
  const bookSearchResults = useQuery(
    api.books.listBooks,
    forkCategory === 'linked-branch' && bookSearchQuery.length >= 2
      ? { searchQuery: bookSearchQuery, limit: 8 }
      : 'skip'
  )

  // Fetch target book's content for landing-point search
  const targetBookData = useQuery(
    api.books.getBook,
    forkTargetBookId ? { bookId: asId<"books">(forkTargetBookId) } : 'skip'
  ) as { rootBranchId?: string } | null | undefined
  const targetRootBranchData = useQuery(
    api.branches.getBranch,
    targetBookData?.rootBranchId ? { branchId: asId<"branches">(targetBookData.rootBranchId) } : 'skip'
  ) as { contentUrl?: string; content?: unknown[] } | null | undefined
  const [targetPlainText, setTargetPlainText] = useState('')
  useEffect(() => {
    if (!forkTargetBookId) { setTargetPlainText(''); return }
    const url = targetRootBranchData?.contentUrl
    const inline = targetRootBranchData?.content
    if (url) {
      fetch(url).then(r => r.json()).then((nodes: unknown[]) => setTargetPlainText(extractText(nodes))).catch(() => {})
    } else if (Array.isArray(inline) && inline.length > 0) {
      setTargetPlainText(extractText(inline))
    }
  }, [forkTargetBookId, targetRootBranchData])

  // Search target book text as user types landing-point query
  const [targetSearchQuery, setTargetSearchQuery] = useState('')
  const [targetSearchOpen, setTargetSearchOpen] = useState(false)
  const targetSearchHits = useMemo(() => {
    if (!targetPlainText || targetSearchQuery.length < 3) return []
    const q = targetSearchQuery.toLowerCase()
    const hits: { excerpt: string; index: number }[] = []
    const textLower = targetPlainText.toLowerCase()
    let startIdx = 0
    while (hits.length < 8) {
      const idx = textLower.indexOf(q, startIdx)
      if (idx === -1) break
      const s = Math.max(0, idx - 120)
      const e = Math.min(targetPlainText.length, idx + targetSearchQuery.length + 120)
      const excerpt = (s > 0 ? '…' : '') + targetPlainText.slice(s, e) + (e < targetPlainText.length ? '…' : '')
      hits.push({ excerpt, index: idx })
      startIdx = idx + targetSearchQuery.length
    }
    return hits
  }, [targetPlainText, targetSearchQuery])

  // ── Edit placement modal for linked branches ──
  const [editPlacement, setEditPlacement] = useState<{
    branchId: string; forkContext: string; forkPosition?: number; targetForkContext: string; targetForkPosition?: number; targetBookId?: string; saving: boolean
  } | null>(null)
  // Search state for edit placement modal
  const [editSourceSearch, setEditSourceSearch] = useState('')
  const [editSourceOpen, setEditSourceOpen] = useState(false)
  const [editTargetSearch, setEditTargetSearch] = useState('')
  const [editTargetOpen, setEditTargetOpen] = useState(false)

  // Fetch target book content for edit placement modal
  const editTargetBookData = useQuery(
    api.books.getBook,
    editPlacement?.targetBookId ? { bookId: asId<"books">(editPlacement.targetBookId) } : 'skip'
  ) as { rootBranchId?: string } | null | undefined
  const editTargetRootBranch = useQuery(
    api.branches.getBranch,
    editTargetBookData?.rootBranchId ? { branchId: asId<"branches">(editTargetBookData.rootBranchId) } : 'skip'
  ) as { contentUrl?: string; content?: unknown[] } | null | undefined
  const [editTargetPlainText, setEditTargetPlainText] = useState('')
  useEffect(() => {
    if (!editPlacement?.targetBookId) { setEditTargetPlainText(''); return }
    const url = editTargetRootBranch?.contentUrl
    const inline = editTargetRootBranch?.content
    if (url) {
      fetch(url).then(r => r.json()).then((nodes: unknown[]) => setEditTargetPlainText(extractText(nodes))).catch(() => {})
    } else if (Array.isArray(inline) && inline.length > 0) {
      setEditTargetPlainText(extractText(inline))
    }
  }, [editPlacement?.targetBookId, editTargetRootBranch])

  // Source book plain text (current book, already loaded)
  const sourcePlainText = useMemo(() => contentNodes.length > 0 ? extractText(contentNodes) : '', [contentNodes])

  // Branch markers for readers (map node indices → child branches)
  const branchMarkers = useMemo(() => {
    if (contentNodes.length === 0 || userBranches.length === 0) return new Map<number, BranchMarker[]>()
    const map = new Map<number, BranchMarker[]>()
    const add = (idx: number, m: BranchMarker) => { if (!map.has(idx)) map.set(idx, []); map.get(idx)!.push(m) }
    const lastIdx = Math.max(0, contentNodes.length - 1)
    for (const child of userBranches) {
      if (child.parentBranchId !== rootBranch?._id) continue
      const marker: BranchMarker = { id: child._id, title: child.title, category: child.category, wordCount: child.wordCount, rating: child.rating }
      const ctx = child.forkContext?.trim()
      if (!ctx) { add(lastIdx, marker); continue }
      const needle = ctx.toLowerCase().slice(0, 50)
      let found = false
      for (let i = 0; i < contentNodes.length; i++) {
        if (extractText([contentNodes[i]]).toLowerCase().includes(needle)) { add(i, marker); found = true; break }
      }
      if (!found) add(lastIdx, marker)
    }
    return map
  }, [contentNodes, userBranches, rootBranch])

  // Search hits for edit placement
  const editSourceHits = useMemo(() => {
    if (!sourcePlainText || editSourceSearch.length < 3) return []
    const q = editSourceSearch.toLowerCase()
    const hits: { excerpt: string; index: number }[] = []
    const textLower = sourcePlainText.toLowerCase()
    let startIdx = 0
    while (hits.length < 8) {
      const idx = textLower.indexOf(q, startIdx)
      if (idx === -1) break
      const s = Math.max(0, idx - 120)
      const e = Math.min(sourcePlainText.length, idx + editSourceSearch.length + 120)
      const excerpt = (s > 0 ? '…' : '') + sourcePlainText.slice(s, e) + (e < sourcePlainText.length ? '…' : '')
      hits.push({ excerpt, index: idx })
      startIdx = idx + editSourceSearch.length
    }
    return hits
  }, [sourcePlainText, editSourceSearch])

  const editTargetHits = useMemo(() => {
    if (!editTargetPlainText || editTargetSearch.length < 3) return []
    const q = editTargetSearch.toLowerCase()
    const hits: { excerpt: string; index: number }[] = []
    const textLower = editTargetPlainText.toLowerCase()
    let startIdx = 0
    while (hits.length < 8) {
      const idx = textLower.indexOf(q, startIdx)
      if (idx === -1) break
      const s = Math.max(0, idx - 120)
      const e = Math.min(editTargetPlainText.length, idx + editTargetSearch.length + 120)
      const excerpt = (s > 0 ? '…' : '') + editTargetPlainText.slice(s, e) + (e < editTargetPlainText.length ? '…' : '')
      hits.push({ excerpt, index: idx })
      startIdx = idx + editTargetSearch.length
    }
    return hits
  }, [editTargetPlainText, editTargetSearch])
  const handleSavePlacement = useCallback(async () => {
    if (!editPlacement) return
    setEditPlacement(p => p ? { ...p, saving: true } : null)
    try {
      await convex.mutation(api.branches.updateLinkedBranchPlacement, {
        branchId: asId<"branches">(editPlacement.branchId),
        forkContext: editPlacement.forkContext || undefined,
        targetForkContext: editPlacement.targetForkContext || undefined,
      })
      showToast({ message: 'Placement updated', type: 'success' })
      setEditPlacement(null)
    } catch (e) {
      showToast({ message: e instanceof Error ? e.message : 'Failed to update', type: 'error' })
      setEditPlacement(p => p ? { ...p, saving: false } : null)
    }
  }, [editPlacement, showToast])

  // ── Character popover (origin story highlight clicks) ──
  const [characterPopover, setCharacterPopover] = useState<{
    name: string; x: number; y: number; stories: OriginStoryInfo[]
  } | null>(null)

  // Desktop: text selection → fork popover
  useEffect(() => {
    if (isTouchDevice) return
    const handler = () => {
      const sel = window.getSelection()
      if (!sel || sel.isCollapsed || !contentRef.current) return
      const text = sel.toString().trim()
      if (text.length < 10) return
      if (!contentRef.current.contains(sel.anchorNode)) return
      const range   = sel.getRangeAt(0)
      const rect    = range.getBoundingClientRect()
      const charOffset = estimateCharOffset(contentRef.current, range)
      setForkPopover({ x: rect.left + rect.width / 2, y: rect.bottom, text, charOffset })
      setBranchHereVisible(false)
    }
    document.addEventListener('mouseup', handler)
    return () => document.removeEventListener('mouseup', handler)
  }, [])

  // Mobile (Android/iOS): text selection → bottom bar (native copy menu prevents mouseup)
  useEffect(() => {
    if (!isTouchDevice) return
    const handler = () => {
      // Delay 400ms to let Android finish showing native selection handles
      setTimeout(() => {
        const sel = window.getSelection()
        if (!sel || sel.isCollapsed || !contentRef.current) { setMobileSelectionBar(null); return }
        const text = sel.toString().trim()
        if (text.length < 10) { setMobileSelectionBar(null); return }
        if (!contentRef.current.contains(sel.anchorNode)) { setMobileSelectionBar(null); return }
        const range      = sel.getRangeAt(0)
        const charOffset = estimateCharOffset(contentRef.current, range)
        setMobileSelectionBar({ text, charOffset })
        setBranchHereVisible(false)
      }, 400)
    }
    document.addEventListener('touchend', handler)
    return () => document.removeEventListener('touchend', handler)
  }, [])

  // Clear mobile bar when selection is dismissed
  useEffect(() => {
    if (!isTouchDevice) return
    const handler = () => {
      const sel = window.getSelection()
      if (!sel || sel.isCollapsed) setMobileSelectionBar(null)
    }
    document.addEventListener('selectionchange', handler)
    return () => document.removeEventListener('selectionchange', handler)
  }, [])

  // ── Fork confirm ──
  const handleForkConfirm = useCallback(async () => {
    if (!forkTitle.trim() || !bookId || !rootBranch) return
    if (!isSignedIn) { openAuthModal(); return }
    const contextText = forkPopover?.text ?? manualExcerpt.trim()
    if (!contextText) return
    if (forkCategory === 'origin-story' && !forkCharacterName.trim()) return
    if (forkCategory === 'linked-branch' && !forkTargetBookId) return
    try {
      const newId = await convex.mutation(api.branches.createBranch, {
        bookId: asId<"books">(bookId),
        parentBranchId: asId<"branches">(rootBranch._id),
        title:          forkTitle.trim(),
        category:       forkCategory,
        visibility:     'private',
        forkContext:    contextText,
        forkPosition:   forkPopover?.charOffset,
        characterName:  forkCategory === 'origin-story' ? forkCharacterName.trim() : undefined,
        targetBookId: asId<"books">(forkCategory === 'linked-branch' ? forkTargetBookId : undefined),
        targetForkContext: forkCategory === 'linked-branch' && forkTargetContext.trim() ? forkTargetContext.trim() : undefined,
      })
      setForkModalOpen(false); setForkTitle(''); setForkPopover(null); setManualExcerpt(''); setForkCharacterName(''); setBranchHereVisible(false)
      setForkTargetBookId(null); setForkTargetBookTitle(''); setForkTargetContext(''); setBookSearchQuery('')
      showToast({ message: 'Branch created! Opening editor…', type: 'success' })
      navigate(`/editor/${newId}`)
    } catch (e) {
      showToast({ message: e instanceof Error ? e.message : 'Failed to create branch', type: 'error' })
    }
  }, [forkTitle, forkCategory, forkCharacterName, forkTargetBookId, forkTargetContext, bookId, rootBranch, forkPopover, manualExcerpt, isSignedIn, openAuthModal, navigate, showToast])

  // ── AI generation ──
  const handleAiGenerate = useCallback(async () => {
    if (!forkTitle.trim() || !bookId || !rootBranch) return
    if (!isSignedIn) { openAuthModal(); return }
    const contextText = forkPopover?.text ?? manualExcerpt.trim()
    if (!contextText) { showToast({ message: 'Set a branching point first', type: 'error' }); return }
    if (!forkBrief.trim()) { showToast({ message: 'Tell the AI what you want to happen', type: 'error' }); return }
    if (forkCategory === 'origin-story' && !forkCharacterName.trim()) { showToast({ message: 'Add a character name', type: 'error' }); return }
    // A linked branch bridges two books — it is placed by hand, not AI-generated
    if (forkCategory === 'linked-branch') { showToast({ message: 'Linked branches can\'t be AI-generated', type: 'error' }); return }
    const price = AI_TIER_OPTIONS.find((t) => t.id === forkTier)!.price
    if (storyCoinBalance < price) { showToast({ message: `You need ${price} StoryCoins (you have ${storyCoinBalance})`, type: 'error' }); return }
    setAiBusy(true)
    try {
      // Throws on failure/refusal; the user is refunded server-side.
      const result = await convex.action(api.ai.generateBranch, {
        bookId: asId<"books">(bookId),
        parentBranchId: asId<"branches">(rootBranch._id),
        title: forkTitle.trim(),
        brief: forkBrief.trim(),
        tier: forkTier,
        category: forkCategory,
        forkContext: contextText,
        forkPosition: forkPopover?.charOffset,
        characterName: forkCategory === 'origin-story' ? forkCharacterName.trim() : undefined,
      })
      setForkModalOpen(false); setForkTitle(''); setForkPopover(null); setManualExcerpt(''); setForkCharacterName(''); setBranchHereVisible(false)
      setForkBrief(''); setForkMode('manual')
      showToast({ message: 'Draft generated! Opening editor…', type: 'success' })
      navigate(`/editor/${result.branchId}`)
    } catch (e) {
      showToast({ message: e instanceof Error ? e.message : 'Generation failed', type: 'error' })
    } finally {
      setAiBusy(false)
    }
  }, [forkTitle, forkBrief, forkTier, forkCategory, forkCharacterName, bookId, rootBranch, forkPopover, manualExcerpt, isSignedIn, openAuthModal, navigate, showToast, storyCoinBalance])

  // ── Purchase ──
  const _handlePurchase = useCallback(async () => {
    if (!activeBranch) return
    if (!isSignedIn) { openAuthModal(); return }
    try {
      await purchaseBranch({ branchId: asId<"branches">(activeBranch._id) })
      showToast({ message: 'Unlocked!', type: 'success' })
    } catch (e) {
      showToast({ message: e instanceof Error ? e.message : 'Purchase failed', type: 'error' })
    }
  }, [activeBranch, isSignedIn, openAuthModal, purchaseBranch, showToast])

  // ── Derived ──
  const isOwnFork    = !!(activeBranch?.parentBranchId && user && user._id === activeBranch.authorId)
  const _isPaidLocked = activeBranch?.visibility === 'paid' && !isOwnFork

  // ── Origin story highlights ──
  const originStoriesMap = React.useMemo(() => {
    const m = new Map<string, OriginStoryInfo[]>()
    for (const entry of (originStoriesData ?? [])) {
      m.set(entry.characterName, entry.stories)
    }
    return m
  }, [originStoriesData])
  const characterNames = React.useMemo(() => Array.from(originStoriesMap.keys()), [originStoriesMap])

  const handleCharacterClick = useCallback((name: string, e: React.MouseEvent) => {
    const stories = originStoriesMap.get(name) ?? []
    setCharacterPopover({ name, x: e.clientX, y: e.clientY, stories })
  }, [originStoriesMap])

  // ── Comment handlers ──
  const isAdmin = canModerate()
  const isOwnBook = user && user._id === book?.importedBy
  const canDeleteComments = isAdmin || (!isPublicDomainBranch && !!isOwnBook)
  // Cover generation: the book's owner (root-branch author) or an admin.
  const canGenerateCover = !!(
    isSignedIn && (isAdmin || (rootBranch?.authorId && rootBranch.authorId === me?._id))
  )

  const handleAddComment = useCallback(
    async (text: string, parentCommentId?: string) => {
      if (!rootBranch || addCommentState === null) return
      await addCommentMutation({
        branchId: asId<"branches">(rootBranch._id),
        paragraphIndex: addCommentState.paragraphIndex,
        text,
        parentCommentId: asId<"paragraph_comments">(parentCommentId),
      })
      showToast({ message: 'Comment posted!', type: 'success' })
      setAddCommentState(null)
      setViewCommentsParagraph(addCommentState.paragraphIndex)
    },
    [rootBranch, addCommentState, addCommentMutation, showToast],
  )

  const handleDeleteComment = useCallback(
    async (commentId: string) => {
      await deleteCommentMutation({ commentId: asId<"paragraph_comments">(commentId) })
      showToast({ message: 'Comment deleted', type: 'success' })
    },
    [deleteCommentMutation, showToast],
  )

  const handleFlagComment = useCallback(
    async (commentId: string) => {
      const result = await flagCommentMutation({ commentId: asId<"paragraph_comments">(commentId) })
      showToast({
        message: result?.alreadyFlagged ? 'Already flagged' : 'Comment flagged for review',
        type: result?.alreadyFlagged ? 'info' : 'success',
      })
    },
    [flagCommentMutation, showToast],
  )

  const handleLikeComment = useCallback(
    async (commentId: string) => {
      if (!isSignedIn) { openAuthModal(); return }
      await likeCommentMutation({ commentId: asId<"paragraph_comments">(commentId) })
    },
    [isSignedIn, likeCommentMutation, openAuthModal],
  )

  const handleDislikeComment = useCallback(
    async (commentId: string) => {
      if (!isSignedIn) { openAuthModal(); return }
      await dislikeCommentMutation({ commentId: asId<"paragraph_comments">(commentId) })
    },
    [isSignedIn, dislikeCommentMutation, openAuthModal],
  )

  const handleBranchFromParagraph = useCallback((nodeIndex: number) => {
    if (!isSignedIn) { openAuthModal(); return }
    const node = contentNodes[nodeIndex]
    const context = extractText([node], false).trim().slice(0, 300)
    setManualExcerpt(context || `Paragraph ${nodeIndex + 1}`)
    setForkModalOpen(true)
  }, [isSignedIn, openAuthModal, contentNodes])

  // ── Loading / error ──
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full" />
      </div>
    )
  }

  if (!book) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center space-y-4">
          <ExclamationCircleIcon className="w-12 h-12 text-red-500 mx-auto" />
          <p className="text-gray-700 dark:text-gray-300">Book not found</p>
          <button onClick={() => navigate(-1)} className="btn btn-primary">Go Back</button>
        </div>
      </div>
    )
  }

  // ── Render ──
  return (
    <div className="flex flex-col" style={{ height: 'calc(100dvh - 64px)' }}>

      {/* ── Character origin-story popover ── */}
      {characterPopover && (
        <div className="fixed inset-0 z-40" onClick={() => setCharacterPopover(null)} />
      )}
      {characterPopover && (
        <div
          className="fixed z-50"
          style={{ left: Math.min(characterPopover.x, window.innerWidth - 280), top: characterPopover.y + 8 }}
        >
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-rose-200 dark:border-rose-800 w-64 p-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-bold text-gray-900 dark:text-gray-100">
                Origin stories — <span className="text-rose-600 dark:text-rose-400">{characterPopover.name}</span>
              </p>
              <button onClick={() => setCharacterPopover(null)} className="text-gray-400 hover:text-gray-600 text-xs">✕</button>
            </div>
            {characterPopover.stories.length === 0 ? (
              <p className="text-xs text-gray-500 italic">No published origin stories yet.</p>
            ) : (
              <ul className="space-y-2">
                {characterPopover.stories.map((s) => (
                  <li key={s._id}>
                    <Link
                      to={`/reading/${s._id}`}
                      onClick={() => setCharacterPopover(null)}
                      className="flex flex-col hover:bg-rose-50 dark:hover:bg-rose-900/20 rounded-lg px-2 py-1.5 transition-colors"
                    >
                      <span className="text-sm font-medium text-gray-800 dark:text-gray-200">{s.title}</span>
                      <span className="text-xs text-gray-500">{s.wordCount.toLocaleString()} words · ★ {s.rating.toFixed(1)}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {/* ── Edit-book modal ── */}
      {editModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-lg p-6 space-y-4">
            <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <PencilIcon className="w-5 h-5 text-primary-500" /> Edit book details
            </h2>

            {/* Cover art picker */}
            <div className="flex items-start gap-4">
              <div className="relative flex-shrink-0 group cursor-pointer" onClick={() => coverFileRef.current?.click()}>
                <img
                  src={editModal.coverPreview ?? book!.coverImage}
                  alt="cover"
                  className="w-20 h-28 object-cover rounded-lg shadow"
                />
                <div className="absolute inset-0 bg-black/40 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <CameraIcon className="w-6 h-6 text-white" />
                </div>
              </div>
              <div className="flex-1 space-y-1">
                <p className="text-xs font-medium text-gray-700 dark:text-gray-300">Cover image</p>
                <p className="text-xs text-gray-400 dark:text-gray-500">Click the thumbnail to upload a new cover (JPG, PNG, WebP).</p>
                <button
                  type="button"
                  onClick={() => coverFileRef.current?.click()}
                  className="text-xs text-primary-600 dark:text-primary-400 hover:underline"
                >
                  {editModal.coverFile ? editModal.coverFile.name : 'Choose file…'}
                </button>
                <input
                  ref={coverFileRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleCoverFilePick}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Title</label>
                <input
                  className="input w-full text-sm"
                  value={editModal.title}
                  onChange={e => setEditModal(m => m ? { ...m, title: e.target.value } : null)}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Author</label>
                <input
                  className="input w-full text-sm"
                  value={editModal.author}
                  onChange={e => setEditModal(m => m ? { ...m, author: e.target.value } : null)}
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
              <textarea
                rows={3}
                className="input w-full text-sm resize-none"
                value={editModal.description}
                onChange={e => setEditModal(m => m ? { ...m, description: e.target.value } : null)}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">Categories</label>
              <div className="flex flex-wrap gap-2">
                {BOOK_GENRES.map(g => (
                  <button key={g} type="button"
                    onClick={() => setEditModal(m => {
                      if (!m) return null
                      const has = m.genres.includes(g)
                      if (has && m.genres.length === 1) return m
                      return { ...m, genres: has ? m.genres.filter(x => x !== g) : [...m.genres, g] }
                    })}
                    className={`px-3 py-1 rounded-full text-xs font-semibold capitalize border-2 transition-colors ${
                      editModal.genres.includes(g)
                        ? `${GENRE_COLORS[g]} border-current`
                        : 'border-transparent bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:border-gray-300'
                    }`}
                  >{g}</button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Source URL <span className="text-gray-400">(where this book was imported from)</span>
              </label>
              <input
                type="url"
                className="input w-full text-sm font-mono"
                placeholder="https://…"
                value={editModal.sourceUrl}
                onChange={e => setEditModal(m => m ? { ...m, sourceUrl: e.target.value } : null)}
              />
            </div>

            {/* In Progress toggle — not available for imported books */}
            {!isImportedBook && (
            <div
              onClick={() => setEditModal(m => m ? { ...m, inProgress: !m.inProgress } : null)}
              className="flex items-start gap-3 cursor-pointer select-none p-3 rounded-xl border-2 border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 transition-colors"
              role="switch"
              aria-checked={editModal.inProgress}
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); setEditModal(m => m ? { ...m, inProgress: !m.inProgress } : null) } }}
            >
              <div className="flex-shrink-0 mt-0.5">
                <div
                  className={`w-10 h-6 rounded-full transition-colors flex items-center px-1 ${editModal.inProgress ? 'bg-blue-500' : 'bg-gray-300 dark:bg-gray-600'}`}
                >
                  <div className={`w-4 h-4 rounded-full bg-white shadow transition-transform ${editModal.inProgress ? 'translate-x-4' : ''}`} />
                </div>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  Mark as In Progress
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  Shows an "In Progress" badge on the storefront to let readers know this book is actively being written. Authors can also set individual branches to "In Progress" to allow paragraph-level comments while writing.
                </p>
              </div>
            </div>
            )}

            <div className="flex gap-3 pt-1">
              <button
                onClick={() => { if (editModal.coverPreview) URL.revokeObjectURL(editModal.coverPreview); setEditModal(null) }}
                disabled={editModal.saving}
                className="btn btn-ghost flex-1"
              >Cancel</button>
              <button
                onClick={handleEditSave}
                disabled={editModal.saving}
                className="btn btn-primary flex-1"
              >{editModal.saving ? 'Saving…' : 'Save changes'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Flag / report modal ── */}
      {flagModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md p-6 space-y-4">
            <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <FlagIcon className="w-5 h-5 text-red-500" />
              {flagModal.type === 'book' ? 'Report copyright violation' : 'Report plagiarism'}
            </h2>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {flagModal.type === 'book'
                ? 'If you believe this book was imported without the proper copyright clearance, let us know. Our team will review it and take appropriate action.'
                : 'If you believe this branch/story has been plagiarised from another source, let us know. Our team will review it.'}
            </p>

            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Reason</label>
              <select
                value={flagModal.reason}
                onChange={e => setFlagModal(m => m ? { ...m, reason: e.target.value as typeof m.reason } : null)}
                className="input w-full text-sm"
              >
                {flagModal.type === 'book' && <option value="copyright">Copyright violation</option>}
                {flagModal.type === 'branch' && <option value="plagiarism">Plagiarism</option>}
                <option value="other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Additional details <span className="text-gray-400">(optional)</span>
              </label>
              <textarea
                value={flagModal.details}
                onChange={e => setFlagModal(m => m ? { ...m, details: e.target.value } : null)}
                rows={3}
                placeholder="e.g. Original source URL, rights holder name…"
                className="input w-full resize-none text-sm"
              />
            </div>

            <div className="flex gap-3 pt-1">
              <button
                onClick={() => setFlagModal(null)}
                disabled={flagModal.submitting}
                className="btn btn-ghost flex-1"
              >
                Cancel
              </button>
              <button
                onClick={handleFlagSubmit}
                disabled={flagModal.submitting}
                className="btn flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold"
              >
                {flagModal.submitting ? 'Submitting…' : 'Submit report'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Fork popover ── */}
      {forkPopover && (
        <div
          className="fixed z-50 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl p-3 w-64 text-sm"
          style={{ left: Math.max(8, Math.min(forkPopover.x - 128, window.innerWidth - 280)), top: forkPopover.y + 8 }}
        >
          <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">Branch the story from here?</p>
          <p className="text-[11px] text-gray-500 italic border-l-2 border-primary-400 pl-2 mb-3 line-clamp-2">
            "{forkPopover.text}"
          </p>
          <div className="flex gap-2">
            <button onClick={() => setForkPopover(null)}
              className="flex-1 text-xs py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-500">
              Cancel
            </button>
            <button
              onClick={() => { setForkModalOpen(true); window.getSelection()?.removeAllRanges() }}
              className="flex-1 flex items-center justify-center gap-1 text-xs py-1.5 rounded-lg bg-primary-600 hover:bg-primary-700 text-white font-semibold"
            >
              <ScissorsIcon className="w-3 h-3" /> Branch here
            </button>
          </div>
        </div>
      )}

      {/* ── Edit Placement modal (linked branches) ── */}
      {editPlacement && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={e => e.target === e.currentTarget && setEditPlacement(null)}>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md p-6 space-y-4">
            <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <span className="text-cyan-500">⇌</span> Edit Bridge Placement
            </h2>
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Departure point (source book excerpt)
              </label>
              {editPlacement.forkContext ? (
                <div className="flex items-start gap-2 px-3 py-2 bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 rounded-lg">
                  <p className="text-xs text-gray-700 dark:text-gray-300 italic flex-1 line-clamp-3">"{editPlacement.forkContext}"</p>
                  <button onClick={() => { setEditPlacement(p => p ? { ...p, forkContext: '' } : null); setEditSourceSearch('') }}
                    className="text-gray-400 hover:text-gray-600 text-xs flex-shrink-0 mt-0.5">✕</button>
                </div>
              ) : (
                <div className="relative">
                  <input
                    value={editSourceSearch}
                    onChange={(e) => { setEditSourceSearch(e.target.value); setEditSourceOpen(true) }}
                    onFocus={() => setEditSourceOpen(true)}
                    className="input w-full text-sm"
                    placeholder={sourcePlainText ? 'Type to search this book…' : 'Loading…'}
                    disabled={!sourcePlainText}
                  />
                  {editSourceOpen && editSourceHits.length > 0 && (
                    <div className="absolute z-30 mt-1 w-full bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg max-h-72 overflow-y-auto">
                      {editSourceHits.map((hit, i) => (
                        <button key={i} onClick={() => {
                          setEditPlacement(p => p ? { ...p, forkContext: hit.excerpt.replace(/^…\n?/, '').replace(/\n?…$/, '').trim() } : null)
                          setEditSourceOpen(false); setEditSourceSearch('')
                        }} className="w-full text-left px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 border-b border-gray-100 dark:border-gray-600 last:border-0">
                          <p className="text-xs text-gray-700 dark:text-gray-200 whitespace-pre-wrap">{hit.excerpt}</p>
                        </button>
                      ))}
                    </div>
                  )}
                  {editSourceOpen && editSourceSearch.length >= 3 && editSourceHits.length === 0 && sourcePlainText && (
                    <div className="absolute z-30 mt-1 w-full bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg p-3 text-xs text-gray-500">
                      No matches found
                    </div>
                  )}
                </div>
              )}
              <p className="text-xs text-gray-400 mt-1">The passage where this bridge departs from the source book.</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Landing point (target book excerpt)
              </label>
              {editPlacement.targetForkContext ? (
                <div className="flex items-start gap-2 px-3 py-2 bg-cyan-50 dark:bg-cyan-900/20 border border-cyan-200 dark:border-cyan-800 rounded-lg">
                  <p className="text-xs text-cyan-800 dark:text-cyan-200 italic flex-1 line-clamp-3">"{editPlacement.targetForkContext}"</p>
                  <button onClick={() => { setEditPlacement(p => p ? { ...p, targetForkContext: '' } : null); setEditTargetSearch('') }}
                    className="text-cyan-400 hover:text-cyan-600 text-xs flex-shrink-0 mt-0.5">✕</button>
                </div>
              ) : (
                <div className="relative">
                  <input
                    value={editTargetSearch}
                    onChange={(e) => { setEditTargetSearch(e.target.value); setEditTargetOpen(true) }}
                    onFocus={() => setEditTargetOpen(true)}
                    className="input w-full text-sm"
                    placeholder={editTargetPlainText ? 'Type to search the target book…' : 'Loading target book…'}
                    disabled={!editTargetPlainText}
                  />
                  {editTargetOpen && editTargetHits.length > 0 && (
                    <div className="absolute z-30 mt-1 w-full bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg max-h-72 overflow-y-auto">
                      {editTargetHits.map((hit, i) => (
                        <button key={i} onClick={() => {
                          setEditPlacement(p => p ? { ...p, targetForkContext: hit.excerpt.replace(/^…\n?/, '').replace(/\n?…$/, '').trim() } : null)
                          setEditTargetOpen(false); setEditTargetSearch('')
                        }} className="w-full text-left px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 border-b border-gray-100 dark:border-gray-600 last:border-0">
                          <p className="text-xs text-gray-700 dark:text-gray-200 whitespace-pre-wrap">{hit.excerpt}</p>
                        </button>
                      ))}
                    </div>
                  )}
                  {editTargetOpen && editTargetSearch.length >= 3 && editTargetHits.length === 0 && editTargetPlainText && (
                    <div className="absolute z-30 mt-1 w-full bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg p-3 text-xs text-gray-500">
                      No matches found in target book
                    </div>
                  )}
                </div>
              )}
              <p className="text-xs text-gray-400 mt-1">Readers will be scrolled to this passage when they follow the link.</p>
            </div>
            <div className="flex gap-3 pt-1">
              <button onClick={() => setEditPlacement(null)} className="btn btn-ghost flex-1">Cancel</button>
              <button onClick={handleSavePlacement} disabled={editPlacement.saving} className="btn btn-primary flex-1">
                {editPlacement.saving ? 'Saving…' : 'Save placement'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Fork modal ── */}
      {coverModalOpen && bookId && (
        <CoverModal
          bookId={bookId}
          balance={storyCoinBalance}
          onClose={() => setCoverModalOpen(false)}
          onDone={() => { setCoverModalOpen(false); showToast({ message: 'Cover updated!', type: 'success' }) }}
        />
      )}
      {forkModalOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md p-6 space-y-4 max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <ScissorsIcon className="w-5 h-5 text-primary-500" /> Create a branch
            </h2>

            {/* Mode toggle — AI writing isn't available for cross-book linked branches */}
            {forkCategory !== 'linked-branch' && (
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => setForkMode('manual')}
                  className={`flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-semibold border-2 transition-all ${
                    forkMode === 'manual'
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                      : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-primary-300'
                  }`}
                >
                  <ScissorsIcon className="w-4 h-4" /> Write it myself
                </button>
                <button
                  onClick={() => setForkMode('ai')}
                  className={`flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-semibold border-2 transition-all ${
                    forkMode === 'ai'
                      ? 'border-violet-500 bg-violet-50 dark:bg-violet-900/20 text-violet-700 dark:text-violet-300'
                      : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-violet-300'
                  }`}
                >
                  <SparklesIcon className="w-4 h-4" /> Let AI write it
                </button>
              </div>
            )}

            {/* Show selected excerpt OR ask for manual one */}
            {forkPopover ? (
              <p className="text-xs italic text-gray-500 border-l-2 border-primary-300 pl-2 line-clamp-2">
                "{forkPopover.text}"
              </p>
            ) : (
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Branching point <span className="text-red-500">*</span>
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1.5">
                  Paste a sentence from the story where your branch diverges. Tip: select text in the story below to set this automatically.
                </p>
                <textarea
                  autoFocus
                  value={manualExcerpt}
                  onChange={(e) => setManualExcerpt(e.target.value)}
                  rows={3}
                  placeholder={`e.g. "She turned left at the crossroads…"`}
                  className="input w-full resize-none text-sm"
                />
              </div>
            )}

            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Branch title <span className="text-red-500">*</span></label>
              <input autoFocus={!!forkPopover} value={forkTitle}
                onChange={e => setForkTitle(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleForkConfirm()}
                className="input w-full" placeholder="e.g. The Hidden Truth" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Category</label>
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(CATEGORY_COLOR).map(([key, colors]) => (
                  <div key={key} className="relative group">
                    <button onClick={() => { setForkCategory(key as BranchCategory); if (key === 'linked-branch') setForkMode('manual') }}
                      className={`w-full py-2 px-3 rounded-lg text-xs font-medium border-2 transition-colors capitalize ${
                        forkCategory === key ? 'border-primary-500' : 'border-transparent'
                      } ${colors.bg} ${colors.text}`}>
                      {key.replace(/-/g, ' ')}
                    </button>
                    <div className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 hidden group-hover:block z-20">
                      <div className="bg-gray-900 dark:bg-gray-700 text-white text-[11px] leading-snug rounded-lg px-2.5 py-1.5 whitespace-nowrap shadow-lg">
                        {CATEGORY_DESCRIPTIONS[key]}
                      </div>
                      <div className="w-2 h-2 bg-gray-900 dark:bg-gray-700 rotate-45 mx-auto -mt-1" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {forkCategory === 'origin-story' && (
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Character name <span className="text-red-500">*</span>
                </label>
                <input
                  value={forkCharacterName}
                  onChange={(e) => setForkCharacterName(e.target.value)}
                  className="input w-full"
                  placeholder="e.g. Elizabeth Bennet"
                />
                <p className="text-xs text-gray-400 mt-1">
                  Their name will be highlighted throughout the book, linking readers to this origin story.
                </p>
              </div>
            )}
            {forkMode === 'ai' && forkCategory !== 'linked-branch' && (
              <>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    What should happen? <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={forkBrief}
                    onChange={(e) => setForkBrief(e.target.value)}
                    rows={4}
                    maxLength={4000}
                    placeholder="Describe what you want the AI to write — events, twists, who's involved, the mood…"
                    className="input w-full resize-none text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Quality</label>
                  <div className="grid grid-cols-3 gap-2">
                    {AI_TIER_OPTIONS.map((t) => (
                      <button
                        key={t.id}
                        onClick={() => setForkTier(t.id)}
                        className={`p-2.5 rounded-lg border-2 text-center transition-all ${
                          forkTier === t.id
                            ? 'border-violet-500 bg-violet-50 dark:bg-violet-900/20'
                            : 'border-gray-200 dark:border-gray-700 hover:border-violet-300'
                        }`}
                      >
                        <p className={`text-xs font-semibold ${forkTier === t.id ? 'text-violet-700 dark:text-violet-300' : 'text-gray-700 dark:text-gray-300'}`}>{t.label}</p>
                        <p className="text-[10px] text-gray-500 dark:text-gray-400">{t.desc}</p>
                        <p className="text-[11px] font-semibold text-gray-700 dark:text-gray-300 mt-1">{t.price} SC</p>
                      </button>
                    ))}
                  </div>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Balance: {storyCoinBalance} StoryCoins. The result is a private draft you can edit before publishing. You won't be charged if generation fails.
                </p>
              </>
            )}
            {forkCategory === 'linked-branch' && (
              <div className="space-y-3">
                {/* Target book picker */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Target book <span className="text-red-500">*</span>
                  </label>
                  {forkTargetBookId ? (
                    <div className="flex items-center gap-2 px-3 py-2 bg-cyan-50 dark:bg-cyan-900/20 border border-cyan-200 dark:border-cyan-800 rounded-lg">
                      <BookOpenIcon className="w-4 h-4 text-cyan-600 flex-shrink-0" />
                      <span className="text-sm text-cyan-800 dark:text-cyan-200 truncate flex-1">{forkTargetBookTitle}</span>
                      <button onClick={() => { setForkTargetBookId(null); setForkTargetBookTitle(''); setBookSearchQuery('') }}
                        className="text-cyan-400 hover:text-cyan-600 text-xs">✕</button>
                    </div>
                  ) : (
                    <div className="relative">
                      <input
                        value={bookSearchQuery}
                        onChange={(e) => { setBookSearchQuery(e.target.value); setBookSearchOpen(true) }}
                        onFocus={() => setBookSearchOpen(true)}
                        className="input w-full"
                        placeholder="Search for a book…"
                      />
                      {bookSearchOpen && bookSearchResults && bookSearchResults.length > 0 && (
                        <div className="absolute z-30 mt-1 w-full bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg max-h-72 overflow-y-auto">
                          {bookSearchResults
                            .filter((b) => b._id !== bookId)
                            .map((b) => (
                              <button key={b._id}
                                onClick={() => {
                                  setForkTargetBookId(b._id)
                                  setForkTargetBookTitle(b.title)
                                  setBookSearchOpen(false)
                                  setBookSearchQuery('')
                                }}
                                className="w-full text-left px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 flex items-center gap-2"
                              >
                                {b.coverImage && <img src={b.coverImage} alt="" className="w-6 h-8 object-cover rounded" />}
                                <div className="min-w-0 flex-1">
                                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{b.title}</p>
                                  <p className="text-[10px] text-gray-500 dark:text-gray-400">{b.originalAuthor}</p>
                                </div>
                              </button>
                            ))}
                        </div>
                      )}
                      {bookSearchOpen && bookSearchQuery.length >= 2 && bookSearchResults && bookSearchResults.filter((b) => b._id !== bookId).length === 0 && (
                        <div className="absolute z-30 mt-1 w-full bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg p-3 text-xs text-gray-500">
                          No books found
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {/* Target context (where the bridge lands in the target book) */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Landing point on target book <span className="text-gray-400">(optional)</span>
                  </label>
                  {forkTargetContext ? (
                    <div className="flex items-start gap-2 px-3 py-2 bg-cyan-50 dark:bg-cyan-900/20 border border-cyan-200 dark:border-cyan-800 rounded-lg">
                      <p className="text-xs text-cyan-800 dark:text-cyan-200 italic flex-1 line-clamp-3">"{forkTargetContext}"</p>
                      <button onClick={() => { setForkTargetContext(''); setTargetSearchQuery('') }}
                        className="text-cyan-400 hover:text-cyan-600 text-xs flex-shrink-0 mt-0.5">✕</button>
                    </div>
                  ) : (
                    <div className="relative">
                      <input
                        value={targetSearchQuery}
                        onChange={(e) => { setTargetSearchQuery(e.target.value); setTargetSearchOpen(true) }}
                        onFocus={() => setTargetSearchOpen(true)}
                        className="input w-full text-sm"
                        placeholder={targetPlainText ? 'Type to search the target book…' : forkTargetBookId ? 'Loading book content…' : 'Select a target book first'}
                        disabled={!forkTargetBookId || !targetPlainText}
                      />
                      {targetSearchOpen && targetSearchHits.length > 0 && (
                        <div className="absolute z-30 mt-1 w-full bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg max-h-72 overflow-y-auto">
                          {targetSearchHits.map((hit, i) => (
                            <button key={i}
                              onClick={() => {
                                setForkTargetContext(hit.excerpt.replace(/^…\n?/, '').replace(/\n?…$/, '').trim())
                                setTargetSearchOpen(false)
                                setTargetSearchQuery('')
                              }}
                              className="w-full text-left px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 border-b border-gray-100 dark:border-gray-600 last:border-0"
                            >
                              <p className="text-xs text-gray-700 dark:text-gray-200 whitespace-pre-wrap">{hit.excerpt}</p>
                            </button>
                          ))}
                        </div>
                      )}
                      {targetSearchOpen && targetSearchQuery.length >= 3 && targetSearchHits.length === 0 && targetPlainText && (
                        <div className="absolute z-30 mt-1 w-full bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg p-3 text-xs text-gray-500">
                          No matches found in target book
                        </div>
                      )}
                    </div>
                  )}
                  <p className="text-xs text-gray-400 mt-1">
                    Readers will be scrolled to this passage when they follow the link.
                  </p>
                </div>
              </div>
            )}
            {aiBusy && forkMode === 'ai' && (
              <div className="flex items-center gap-2.5 rounded-xl bg-violet-50 dark:bg-violet-900/20 px-3 py-2.5">
                <span className="w-4 h-4 rounded-full border-2 border-violet-300 border-t-violet-600 animate-spin" />
                <span className="text-sm font-medium text-violet-700 dark:text-violet-300">{aiProgressLabel}</span>
                <span className="ml-auto text-xs tabular-nums text-violet-400">{aiElapsed}s</span>
              </div>
            )}
            <div className="flex gap-3 pt-1">
              <button disabled={aiBusy} onClick={() => { setForkModalOpen(false); setForkPopover(null); setManualExcerpt(''); setForkCharacterName(''); setForkTargetBookId(null); setForkTargetBookTitle(''); setForkTargetContext(''); setBookSearchQuery(''); setForkBrief(''); setForkMode('manual') }} className="btn btn-ghost flex-1">Cancel</button>
              {forkMode === 'ai' && forkCategory !== 'linked-branch' ? (
                <button
                  onClick={handleAiGenerate}
                  disabled={aiBusy || !forkTitle.trim() || (!forkPopover && !manualExcerpt.trim()) || !forkBrief.trim() || (forkCategory === 'origin-story' && !forkCharacterName.trim()) || storyCoinBalance < AI_TIER_OPTIONS.find((t) => t.id === forkTier)!.price}
                  className="btn flex-1 bg-violet-600 hover:bg-violet-700 disabled:bg-violet-400 text-white flex items-center justify-center gap-1.5"
                >
                  <SparklesIcon className="w-4 h-4" />
                  {aiBusy ? 'Generating…' : `Generate · ${AI_TIER_OPTIONS.find((t) => t.id === forkTier)!.price} SC`}
                </button>
              ) : (
                <button
                  onClick={handleForkConfirm}
                  disabled={!forkTitle.trim() || (!forkPopover && !manualExcerpt.trim()) || (forkCategory === 'origin-story' && !forkCharacterName.trim()) || (forkCategory === 'linked-branch' && !forkTargetBookId)}
                  className="btn btn-primary flex-1"
                >
                  Create branch
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════
          TOP PANEL
          ══════════════════════════════════════════════════ */}
      <div className="flex-shrink-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">

        {/* Page header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-700/50">
          <div className="flex items-center gap-3 min-w-0">
            <button onClick={() => navigate(-1)} className="text-gray-500 hover:text-gray-800 dark:hover:text-gray-200 flex-shrink-0">
              <ArrowLeftIcon className="w-5 h-5" />
            </button>
            {/* Cover thumbnail — editor sees a camera-icon overlay */}
            <div className={`relative flex-shrink-0 ${isBookEditor ? 'group cursor-pointer' : ''}`}
              onClick={() => isBookEditor && openEditModal()}>
              {book.coverImage
                ? <img src={book.coverImage} alt={book.title} className="w-8 h-11 object-cover rounded shadow" />
                : <div className="w-8 h-11 rounded shadow bg-gray-200 dark:bg-gray-700" />}
              {isBookEditor && (
                <div className="absolute inset-0 bg-black/40 rounded opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <CameraIcon className="w-4 h-4 text-white" />
                </div>
              )}
            </div>
            <div className="min-w-0">
              <h1 className="text-sm font-bold text-gray-900 dark:text-gray-100 truncate">{book.title}</h1>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                by {book.originalAuthor} · {userBranches.length} branch{userBranches.length !== 1 ? 'es' : ''}
                {chapters.length > 0 && (() => {
                  const minLevel = Math.min(...chapters.map(c => c.level))
                  const count = chapters.filter(c => c.level === minLevel).length
                  return ` · ${count} chapter${count !== 1 ? 's' : ''}`
                })()}
              </p>
              {book.inProgress && (
                <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300">
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />
                  In Progress
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Generate AI cover — book owner or admin */}
            {canGenerateCover && (
              <button
                title="Generate a cover with AI"
                onClick={() => setCoverModalOpen(true)}
                className="p-1.5 rounded-lg text-gray-400 hover:text-violet-600 dark:hover:text-violet-400 transition-colors"
              >
                <SparklesIcon className="w-4 h-4" />
              </button>
            )}
            {/* Edit metadata button — importer/admin only */}
            {isBookEditor && (
              <button
                title="Edit book details"
                onClick={openEditModal}
                className="p-1.5 rounded-lg text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
              >
                <PencilIcon className="w-4 h-4" />
              </button>
            )}
            {/* Flag book for copyright — only for signed-in users */}
            {isSignedIn && (
              <FollowContentButton targetType="book" targetId={book._id} compact />
            )}
            {isSignedIn && (
              <button
                title={bookReport ? 'Already reported' : 'Report copyright violation'}
                onClick={() => {
                  if (bookReport) return
                  setFlagModal({ type: 'book', targetId: book._id, reason: 'copyright', details: '', submitting: false })
                }}
                className={`p-1.5 rounded-lg transition-colors ${
                  bookReport
                    ? 'text-red-400 dark:text-red-500 cursor-default'
                    : 'text-gray-400 hover:text-red-500 dark:hover:text-red-400'
                }`}
              >
                <FlagIcon className="w-4 h-4" />
              </button>
            )}
            <button
              onClick={() => setReadingMode(true)}
              className="btn btn-sm flex items-center gap-1 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800"
              disabled={contentNodes.length === 0}
            >
              <BookOpenIcon className="w-4 h-4" /> Read
            </button>
            <button
              onClick={() => {
                if (!isSignedIn) { openAuthModal(); return }
                setForkPopover(null)
                setManualExcerpt('')
                setForkTitle('')
                setForkModalOpen(true)
              }}
              className="btn btn-primary btn-sm flex items-center gap-1"
            >
              <PlusIcon className="w-4 h-4" /> Branch
            </button>
          </div>
        </div>

        {/* Git-lane graph */}
        <div ref={graphSectionRef} className="border-b border-gray-100 dark:border-gray-700/50 w-full">
          {/* Collapse toggle bar */}
          <button
            onClick={() => setGraphCollapsed(c => !c)}
            className="w-full flex items-center justify-between px-3 py-1 bg-gray-950/[0.02] dark:bg-gray-900/40 hover:bg-gray-950/[0.05] dark:hover:bg-gray-800/60 transition-colors text-xs text-gray-400 dark:text-gray-500 select-none"
            aria-label={graphCollapsed ? 'Show branches' : 'Hide branches'}
          >
            <span className="font-medium tracking-wide uppercase" style={{ fontSize: '10px', letterSpacing: '0.08em' }}>Branches</span>
            <ChevronDownIcon
              className="w-3.5 h-3.5 transition-transform duration-200"
              style={{ transform: graphCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)' }}
            />
          </button>
          {/* Graph panel */}
          <div
            ref={graphContainerRef}
            className="bg-gray-950/[0.02] dark:bg-gray-900/40 w-full overflow-hidden transition-all duration-200"
            style={{ height: graphCollapsed ? 0 : GRAPH_H }}
          >
            {rootBranch ? (
              <GitLaneGraph
                rootBranch={rootBranch}
                branches={userBranches}
                chapters={chapters}
                totalChars={Math.max(rootCharCount, rootBranch.wordCount * 5, 1)}
                containerWidth={containerWidth}
                onBranchClick={handleBranchClick}
                onChapterClick={handleChapterClick}
                activeBranchId={activeBranch?._id}
                userId={user?._id ?? ''}
                readingProgress={readingProgress}
              />
            ) : (
              <div className="h-full flex items-center justify-center text-sm text-gray-400">Loading…</div>
            )}
          </div>
        </div>

        {/* Incoming linked branches from other books */}
        {incomingLinksData && incomingLinksData.length > 0 && (
          <div className="px-4 py-2 bg-cyan-50/50 dark:bg-cyan-900/10 border-b border-cyan-200/50 dark:border-cyan-800/30">
            <p className="text-[10px] font-semibold text-cyan-600 dark:text-cyan-400 uppercase tracking-wide mb-1.5">
              Bridges from other books ({incomingLinksData.length})
            </p>
            <div className="flex gap-2 overflow-x-auto pb-1">
              {incomingLinksData.map(link => (
                <Link key={link._id} to={`/reading/${link._id}`}
                  className="flex-shrink-0 flex items-center gap-2 px-3 py-1.5 bg-white dark:bg-gray-800 rounded-lg border border-cyan-200 dark:border-cyan-800 hover:border-cyan-400 transition-colors text-xs">
                  <span className="text-cyan-500">⇌</span>
                  <span className="font-medium text-gray-800 dark:text-gray-200 truncate max-w-[120px]">{link.title}</span>
                  <span className="text-gray-400 dark:text-gray-500 truncate max-w-[100px]">from {link.sourceBookTitle}</span>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Selected branch info bar */}
        {activeBranch && (
          <div className="flex items-center gap-3 px-4 py-2 bg-gray-50 dark:bg-gray-800/60 text-xs text-gray-600 dark:text-gray-400 overflow-x-auto">
            <div className="relative group flex-shrink-0">
              <span className={`px-2 py-0.5 rounded-full font-semibold text-[11px] ${cat(activeBranch.category).bg} ${cat(activeBranch.category).text}`}>
                {activeBranch.category.replace(/-/g, ' ')}
              </span>
              {CATEGORY_DESCRIPTIONS[activeBranch.category] && (
                <div className="pointer-events-none absolute bottom-full left-0 mb-1.5 hidden group-hover:block z-20">
                  <div className="bg-gray-900 dark:bg-gray-700 text-white text-[11px] leading-snug rounded-lg px-2.5 py-1.5 whitespace-nowrap shadow-lg">
                    {CATEGORY_DESCRIPTIONS[activeBranch.category]}
                  </div>
                <div className="w-2 h-2 bg-gray-900 dark:bg-gray-700 rotate-45 ml-3 -mt-1" />
              </div>
              )}
            </div>
            <span className="font-semibold text-gray-800 dark:text-gray-200 truncate max-w-[35%]">{activeBranch.title}</span>
            {activeBranch.forkContext && (
              <span className="italic text-gray-400 truncate max-w-[25%] hidden sm:block">
                from: "{activeBranch.forkContext}"
              </span>
            )}
            <span className="ml-auto flex items-center gap-3 flex-shrink-0">
              {/* Interactive star rating — available to all signed-in users */}
              {isSignedIn
                ? (
                  <span className="flex items-center gap-0.5" onMouseLeave={() => setRatingHover(0)}>
                    {[1, 2, 3, 4, 5].map(star => (
                      <button
                        key={star}
                        type="button"
                        title={`Rate ${star} star${star !== 1 ? 's' : ''}`}
                        onMouseEnter={() => setRatingHover(star)}
                        onClick={async () => {
                          try {
                            await rateBranch({ branchId: asId<"branches">(activeBranch._id), rating: star })
                          } catch (e) {
                            showToast({ message: e instanceof Error ? e.message : 'Failed to rate', type: 'error' })
                          }
                        }}
                        className="transition-colors"
                      >
                        <StarSolid className={`w-3.5 h-3.5 ${
                          star <= (ratingHover || myBranchRating?.rating || 0)
                            ? 'text-amber-400'
                            : 'text-gray-300 dark:text-gray-600'
                        }`} />
                      </button>
                    ))}
                    {activeBranch.ratingCount > 0 && (
                      <span className="text-xs text-gray-500 ml-1">
                        {activeBranch.rating.toFixed(1)} ({activeBranch.ratingCount})
                      </span>
                    )}
                  </span>
                )
                : activeBranch.rating > 0 && (
                  <span className="flex items-center gap-0.5">
                    <StarSolid className="w-3 h-3 text-amber-400" />
                    {activeBranch.rating.toFixed(1)}
                  </span>
                )
              }
              <div className="flex items-center gap-0.5 border border-gray-200 dark:border-gray-600 rounded-lg px-0.5">
                <button
                  onClick={() => fontSizeIdx > 0 && setFontSize(FONT_SIZES[fontSizeIdx - 1])}
                  disabled={fontSizeIdx === 0}
                  className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                  aria-label="Decrease font size"
                >
                  <MinusIcon className="w-3.5 h-3.5" />
                </button>
                <span className="text-[10px] font-medium text-gray-500 dark:text-gray-400 px-1 select-none" title={FONT_SIZE_LABELS[fontSize]}>A</span>
                <button
                  onClick={() => fontSizeIdx < FONT_SIZES.length - 1 && setFontSize(FONT_SIZES[fontSizeIdx + 1])}
                  disabled={fontSizeIdx === FONT_SIZES.length - 1}
                  className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                  aria-label="Increase font size"
                >
                  <PlusIcon className="w-3.5 h-3.5" />
                </button>
              </div>
              {activeBranch.visibility === 'paid' && (
                <span className="flex items-center gap-1 text-amber-600 dark:text-amber-400">
                  <LockClosedIcon className="w-3 h-3" /> {activeBranch.price} SC
                </span>
              )}
              {activeBranch.visibility === 'private' && (
                <span className="italic text-gray-400">draft</span>
              )}
              {/* Edit available for own forks or admins */}
              {(isOwnFork || (canModerate() && activeBranch.isImported)) && (
                <Link to={`/editor/${activeBranch._id}`}
                  className="flex items-center gap-1 text-primary-600 dark:text-primary-400 hover:underline">
                  <PencilIcon className="w-3 h-3" /> Edit
                </Link>
              )}
              {isOwnFork && activeBranch.category === 'linked-branch' && (
                <button
                  onClick={() => { setEditPlacement({
                    branchId: activeBranch._id,
                    forkContext: activeBranch.forkContext ?? '',
                    forkPosition: activeBranch.forkPosition,
                    targetForkContext: activeBranch.targetForkContext ?? '',
                    targetForkPosition: activeBranch.targetForkPosition,
                    targetBookId: activeBranch.targetBookId,
                    saving: false,
                  }); setEditSourceSearch(''); setEditTargetSearch('') }}
                  className="flex items-center gap-1 text-cyan-600 dark:text-cyan-400 hover:underline"
                >
                  ⇌ Edit placement
                </button>
              )}
              {activeBranch.parentBranchId && (
                <Link to={`/reading/${activeBranch._id}`}
                  className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400 hover:underline">
                  <BookOpenIcon className="w-3 h-3" /> Read branch
                </Link>
              )}
              {/* Flag branch for plagiarism — available to signed-in readers on user branches */}
              {isSignedIn && activeBranch.parentBranchId && !isOwnFork && (
                <button
                  title={branchReport ? 'Already reported' : 'Report plagiarism'}
                  onClick={() => {
                    if (branchReport) return
                    setFlagModal({ type: 'branch', targetId: activeBranch._id, reason: 'plagiarism', details: '', submitting: false })
                  }}
                  className={`flex items-center gap-1 transition-colors ${
                    branchReport
                      ? 'text-red-400 dark:text-red-500 cursor-default'
                      : 'text-gray-400 hover:text-red-500 dark:hover:text-red-400'
                  }`}
                >
                  <FlagIcon className="w-3 h-3" />
                  {branchReport ? 'Reported' : 'Report'}
                </button>
              )}
            </span>
          </div>
        )}
      </div>

      {/* ── \"Branch here\" floating button (shown after 2s scroll pause) ── */}
      {branchHereVisible && branchHereAnchor && (
        <div className={`pointer-events-none fixed inset-0 z-30 flex items-end ${isTouchDevice ? 'justify-center pb-8' : 'justify-end pb-6 pr-6'}`}>
          <button
            onClick={handleBranchHereClick}
            className="pointer-events-auto flex items-center gap-2 px-4 py-2.5 rounded-full bg-primary-600 hover:bg-primary-700 active:scale-95 text-white text-sm font-semibold shadow-xl transition-all duration-200"
          >
            <ScissorsIcon className="w-4 h-4" />
            Branch here
          </button>
        </div>
      )}

      {/* ── Mobile selection bar: shown when user selects text on Android/iOS ── */}
      {mobileSelectionBar && (
        <div className="fixed bottom-0 inset-x-0 z-40">
          <div className="bg-primary-700 text-white px-4 py-3 flex items-center gap-3 shadow-2xl">
            <ScissorsIcon className="w-5 h-5 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold leading-tight">Branch from selection</p>
              <p className="text-xs opacity-75 truncate">&ldquo;{mobileSelectionBar.text}&rdquo;</p>
            </div>
            <button
              onClick={() => {
                setForkPopover({ x: 0, y: 0, text: mobileSelectionBar.text, charOffset: mobileSelectionBar.charOffset })
                setMobileSelectionBar(null)
                setForkModalOpen(true)
                window.getSelection()?.removeAllRanges()
              }}
              className="shrink-0 bg-white text-primary-700 rounded-lg px-3 py-1.5 text-sm font-bold"
            >
              Branch here
            </button>
            <button
              onClick={() => { setMobileSelectionBar(null); window.getSelection()?.removeAllRanges() }}
              className="shrink-0 text-white/70 hover:text-white text-xl leading-none px-1"
            >&#x2715;</button>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════
          BOTTOM PANEL: always shows root/main text
          ══════════════════════════════════════════════════ */}
      <div ref={contentPanelRef} className="flex-1 overflow-y-auto bg-gray-50 dark:bg-gray-900" style={{ overscrollBehavior: 'none' }}>
        {isLoadingContent ? (
          <div className="flex items-center justify-center h-40">
            <div className="animate-spin w-6 h-6 border-4 border-primary-500 border-t-transparent rounded-full" />
          </div>
        ) : contentNodes.length > 0 ? (
          <div className="max-w-3xl mx-auto px-4 sm:px-8 py-10">
            {/* Show selected branch context banner when a user fork is selected */}
            {activeBranch?.parentBranchId && activeBranch.forkContext && (
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4 mb-8 flex gap-3">
                <ScissorsIcon className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs font-semibold text-amber-700 dark:text-amber-300 mb-1">
                    "{activeBranch.title}" branches from here
                  </p>
                  <p className="text-sm text-amber-800 dark:text-amber-200 italic">"{activeBranch.forkContext}"</p>
                  <Link to={`/reading/${activeBranch._id}`}
                    className="text-xs text-amber-600 dark:text-amber-400 underline mt-1 inline-flex items-center gap-1">
                    <BookOpenIcon className="w-3 h-3" /> Read this branch
                  </Link>
                </div>
              </div>
            )}

            <article ref={contentRef}>
              <SlateRenderer
                content={contentNodes}
                fontSize={fontSize}
                fontFamily="serif"
                characterNames={characterNames}
                onCharacterClick={handleCharacterClick}
                inProgress={commentsEnabled}
                commentCounts={commentCounts}
                onAddComment={(nodeIndex) => {
                  if (!isSignedIn) { openAuthModal(); return }
                  setAddCommentState({ paragraphIndex: nodeIndex })
                }}
                onViewComments={(nodeIndex) => setViewCommentsParagraph(nodeIndex)}
                onBranchFromParagraph={canBranchPerParagraph ? handleBranchFromParagraph : undefined}
                branchMarkers={branchMarkers}
              />
            </article>

            {/* Footer fork CTA */}
            <div className="mt-16 border-t border-gray-200 dark:border-gray-700 pt-8 text-center">
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                Want to tell this story differently?
              </p>
              <button
                onClick={() => { if (!isSignedIn) { openAuthModal(); return } setForkModalOpen(true) }}
                className="btn btn-primary flex items-center gap-2 mx-auto"
              >
                <ScissorsIcon className="w-4 h-4" /> Create your own branch
              </button>
              <p className="text-xs text-gray-400 mt-2">Select any text above to branch from that exact moment</p>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-40 text-gray-400">
            <BookOpenIcon className="w-8 h-8 mb-2" />
            <p className="text-sm">No content available.</p>
          </div>
        )}
      </div>

      {/* ── Immersive reading mode overlay ── */}
      {readingMode && rootBranch && (
        <ImmersiveReader
          branchId={rootBranch._id}
          contentNodes={contentNodes}
          chapters={chapters}
          bookTitle={book?.title ?? ''}
          branchTitle={activeBranch?.title ?? book?.title ?? ''}
          branchMarkers={branchMarkers}
          onClose={() => setReadingMode(false)}
        />
      )}

      {/* ── Comment modals ── */}
      {addCommentState !== null && (
        <AddCommentModal
          paragraphIndex={addCommentState.paragraphIndex}
          replyTo={addCommentState.replyTo}
          onSubmit={handleAddComment}
          onClose={() => setAddCommentState(null)}
        />
      )}
      {viewCommentsParagraph !== null && (() => {
        const paraComments = comments.filter((c) => c.paragraphIndex === viewCommentsParagraph)
        return (
          <ViewCommentsModal
            paragraphIndex={viewCommentsParagraph}
            comments={paraComments}
            currentUserId={user?._id}
            isAuthor={canDeleteComments}
            onDelete={handleDeleteComment}
            onFlag={handleFlagComment}
            onLike={handleLikeComment}
            onDislike={handleDislikeComment}
            onClose={() => setViewCommentsParagraph(null)}
            onAddComment={(replyTo) => {
              setViewCommentsParagraph(null)
              setAddCommentState({ paragraphIndex: viewCommentsParagraph, replyTo })
            }}
          />
        )
      })()}
    </div>
  )
}

export default BookPage

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** A Slate node as stored — only the fields the outline/offset helpers look at. */
type LooseNode = { type?: string; text?: string; children?: LooseNode[] }

function extractText(nodes: unknown[], topLevel = true): string {
  return (nodes as LooseNode[]).map(node => {
    if (typeof node.text === 'string') return node.text
    if (Array.isArray(node.children)) return extractText(node.children, false)
    return ''
  }).join(topLevel ? '\n' : '')
}

function extractChapters(nodes: unknown[]): Chapter[] {
  const result: Chapter[] = []
  let charOffset = 0
  let domIndex   = 0
  for (const node of nodes as LooseNode[]) {
    const t = node.type ?? ''
    const isH1 = t === 'heading-one'
    const isH2 = t === 'heading-two'
    const isH3 = t === 'heading-three'
    const text  = extractText([node])
    if (isH1 || isH2 || isH3) {
      result.push({
        title:      text.trim().slice(0, 60),
        charOffset,
        domIndex,
        level:      isH1 ? 1 : isH2 ? 2 : 3,
      })
      domIndex++
    }
    charOffset += text.length + 1   // +1 for block separator
  }
  return result
}

function estimateCharOffset(container: HTMLElement, range: Range): number {
  const pre = document.createRange()
  pre.selectNodeContents(container)
  pre.setEnd(range.startContainer, range.startOffset)
  return pre.toString().length
}

